import { CSSProperties, ReactNode } from 'react';
import { colors } from '../../theme/colors';
import { fontSize } from '../../theme/typography';
import spacing from '../../theme/spacing';
import { borderRadius } from '../../theme/borderRadius';

export interface NotificationIndicatorProps {
  isShown?: boolean;
  style?: CSSProperties;
  size?: number;
  type?: 'primary' | 'yellow' | 'red' | 'warning' | 'lightWithDarkText';
  count?: number;
  text?: string | ReactNode;
}

const containerWithContentStyles: CSSProperties = {
  height: 'auto',
  width: 'auto',
  minWidth: 'auto',
  padding: `${spacing.xxs} ${spacing.small}`,
  borderRadius: borderRadius.large,
};

const textStyles: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: fontSize.xxs + 0.5,
  fontWeight: 'bold',
  position: 'relative',
};

const NotificationIndicator = ({
  isShown,
  style,
  size = 8,
  type = 'primary',
  count,
  text,
}: NotificationIndicatorProps) => {
  if (!isShown) {
    return null;
  }

  const getBackgroundColor = () => {
    switch (type) {
      case 'primary':
        return `linear-gradient(to bottom right, ${colors.primary}, ${colors.primaryLight}, ${colors.primaryLight}, ${colors.primaryVeryLight})`;
      case 'warning':
        return `linear-gradient(to bottom right, ${colors.yellowLight}, ${colors.yellowLight})`;
      case 'yellow':
        return `linear-gradient(to bottom right, ${colors.yellow}, ${colors.yellowLight}, ${colors.yellowVeryLight}, ${colors.yellowSuperLight})`;
      case 'red':
        return `linear-gradient(to bottom right, ${colors.redNotification}, ${colors.redNotification})`;
      case 'lightWithDarkText':
        return colors.textPrimary;
      default:
        return colors.primary;
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'warning':
        return colors.backgroundMediumLight;
      case 'lightWithDarkText':
        return colors.backgroundHard;
      default:
        return colors.textPrimary;
    }
  };

  const hasContent = Boolean(count || text);
  const displayCount = count && count > 99 ? '99+' : count;

  return (
    <div
      style={{
        position: 'absolute',
        height: size,
        width: size,
        minWidth: size,
        borderRadius: '50%',
        ...(hasContent ? containerWithContentStyles : {}),
        background: getBackgroundColor(),
        color: getTextColor(),
        border: `2px solid ${colors.backgroundMediumLight}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      {hasContent && <div style={textStyles}>{text || displayCount}</div>}
    </div>
  );
};

export default NotificationIndicator;
